<script>
    import { createEventDispatcher, onMount } from "svelte";
    import { CLIENT_GROUP_MAP } from "../../../../js/clientCurrencyMap.js";
    import moment from "moment"; moment.locale("es");
    import { normalizeCampaignRow, sortAndNumber, getStatusInfo } from "../../../utils/freebetsCore.js";
    import { computeCampaignStatus, planActionsForCampaign, CAMPAIGN_STATUS, getCampaignStatusOptions } from "../../../utils/statusEngine.js";
    import { getClientFilterCodes } from "../../../../js/credentialsApi.js";

    // ========== API ==========
    export let provider = "";
    export let api = {
        listByProvider: async (_provider) => { throw new Error("api.listByProvider no inyectado"); },
        cancelCampaign: async (_campaignId, _campaign) => { throw new Error("api.cancelCampaign no inyectado"); },
        getById: async (_campaignId) => null,
    };
    export async function reconcileOne(campaignId){
        const freshBase = await refetchOne(campaignId);
        if (!freshBase) return;
        const startMs = freshBase.startDate ? new Date(freshBase.startDate).getTime() : (freshBase.startTime || 0);
        const endMs   = freshBase.endDate   ? new Date(freshBase.endDate).getTime()   : 0;
        const baseStatus = Number(freshBase.statusCode ?? 4);
        const effective = computeCampaignStatus(baseStatus, startMs, endMs);
        const info = getStatusInfo(effective);
        const rowForPlan = { ...freshBase, baseStatus };
        await executePlans([rowForPlan]);
        campaigns = (campaigns || []).map(r =>
            String(r.campaignId) === String(campaignId)
                ? { ...r,
                    baseStatus,
                    statusCode: effective,
                    statusInfoClass: info.class,
                    statusInfoLabel: info.label
                }
                : r
        );
        return { ...rowForPlan, statusCode: effective };
    }

    async function getSessionClientsFromSession() {
        const allowedCodes = await getClientFilterCodes();
        if (allowedCodes === null) return { ids: [], codes: [] };
        if (allowedCodes.length) return { ids: [], codes: allowedCodes };
        return { ids: [], codes: [] };
    }

    // Exports
    export let icons = {};
    export let showCreate = true;
    export let allowCancel = true;
    export let timeFormat = "DD/MM, hh:mmA";
    export let initialSearch = "";
    export let extraParams = {};
    export async function refresh(){ return load(); }
    const dispatch = createEventDispatcher();

    // ========== Pagination ==========
    let pageSizeOptions = [10, 50, 100, 200];
    let pageSize = 10;
    let page = 1;
    let pagerInfo = { total: 0, totalPages: 1, pageStart: 0, pageEnd: 0 };
    let numberAt = (iLocal) => iLocal + 1;

    // ========== Status ==========
    let isLoading = false;
    let sortAsc = false;
    let search = initialSearch;
    let campaigns = [];
    let _rows = [];
    let _persistingPlayerKeys = new Set();
    let _providerDeletingKeys = new Set();
    let _loadSeq = 0;
    const TYPE_OPTIONS = ["SLOT", "LIVE"];
    const STATUS_OPTIONS = getCampaignStatusOptions();
    const ALL_STATUS_VALUES = STATUS_OPTIONS.map(o => o.value);
    const DEFAULT_STATUS_VALUES = [
        CAMPAIGN_STATUS.ACTIVE,
        CAMPAIGN_STATUS.SCHEDULED
    ];

    // ====== DEBOUNCE SEARCH ======
    function clearAllFilters(){
        filters = {
            client: "",
            campaignName: "",
            type: "",
            statuses: [...DEFAULT_STATUS_VALUES],
            game: "",
            currency: "",
            dateFrom: "",
            dateTo: "",
            player: "",
            bonus: ""
        };
        scheduleAutoLoad(0);
        showMoreFilters = false;
    }

    // ====== STATUS MULTIPLE ======
    let statusOpen = false;
    function toggleAll(checked){
        filters.statuses = checked ? [...ALL_STATUS_VALUES] : [];
    }
    $: statusesKey = filters.statuses.join(',');
    $: statusSummary =
        (filters.statuses.length === 0 || allSelected)
            ? "Todos"
            : STATUS_OPTIONS
                .filter(o => filters.statuses.includes(o.value))
                .map(o => o.label)
                .join(", ");

    function clickOutside(node) {
        const onPointerDown = (e) => {
            if (statusOpen && node && !node.contains(e.target)) {
                statusOpen = false;
            }
        };
        document.addEventListener('pointerdown', onPointerDown, true);
        return {
            destroy() {
                document.removeEventListener('pointerdown', onPointerDown, true);
            }
        };
    }
    // ====== DEBOUNCE SEARCH ======
    let autoSearch = false;
    let _autoTimer = null;
    function scheduleAutoLoad(ms = 350){
        if (!autoSearch) return;
        clearTimeout(_autoTimer);
        _autoTimer = setTimeout(() => { load(); }, ms);
    }
    let _filtersToken = "";
    $: _filtersToken = JSON.stringify(filters);

    // ========== FILTERS ==========
    let showMoreFilters = false;
    let filters = {
        client: "",
        campaignName: "",
        type: "",
        statuses: [...DEFAULT_STATUS_VALUES],
        game: "",
        currency: "",
        dateFrom: "",
        dateTo: "",
        player: "",
        bonus: ""
    };


    $: allSelected = filters.statuses.length > 0 && ALL_STATUS_VALUES.every(v => filters.statuses.includes(v));

    $: clientOptions   = Array.from(new Set((_rows || []).map(p => p.clientLabel).filter(Boolean)));
    $: currencyOptions = Array.from(new Set((_rows || []).map(p => p.currencyLabel).filter(Boolean)));

    function clean(obj) {
        const out = {};
        for (const [k, v] of Object.entries(obj || {})) {
            if (v === 0 || v === false || (v && String(v).trim() !== "")) out[k] = v;
        }
        return out;
    }

    function filtersForServer() {
        return clean({
            client: filters.client,
            campaignName: filters.campaignName,
            type: filters.type,
            status: (filters.statuses.length ? filters.statuses : undefined),
            game: filters.game,
            currency: filters.currency,
            dateFrom: filters.dateFrom ? new Date(filters.dateFrom).toISOString() : undefined,
            dateTo: filters.dateTo ? new Date(filters.dateTo).toISOString() : undefined
        });
    }

    function passesFilters(r){
        const has = (a,b) => !b || (String(a||"").toLowerCase().includes(String(b).toLowerCase()));
        const eq  = (a,b) => !b || String(a||"").toLowerCase() === String(b).toLowerCase();
        const clientOk =
            !filters.client ||
            has(r.clientLabel, filters.client) ||
            has(r?.client?.clientCode, filters.client) ||
            has(r?.client?.clientId, String(filters.client));
        const nameOk   = has(r.campaignName, filters.campaignName);
        const typeOk   = !filters.type || String(r.campaignType).toUpperCase() === String(filters.type).toUpperCase();
        const statusOk = !filters.statuses?.length || filters.statuses.includes(+r.statusCode);
        const gameOk   = has(r.gameLabel, filters.game);
        const currOk   = eq(r.currencyLabel, filters.currency);
        const fromMs = filters.dateFrom ? new Date(filters.dateFrom).getTime() : null;
        const toMs   = filters.dateTo   ? new Date(filters.dateTo).getTime()   : null;
        const start  = r.startDate ? new Date(r.startDate).getTime() : (r.startTime||0);
        const end    = r.endDate   ? new Date(r.endDate).getTime()   : Number.MAX_SAFE_INTEGER;
        let rangeOk = true;
        if (fromMs!==null && toMs!==null) rangeOk = !(end < fromMs || start > toMs);
        else if (fromMs!==null) rangeOk = end >= fromMs;
        else if (toMs!==null) rangeOk = start <= toMs;
        const playerText = norm(filters.player);
        const bonusText  = norm(filters.bonus);
        let playerOk = true;
        let bonusOk  = true;
        if (playerText) {
            playerOk = Array.isArray(r.playerList) && r.playerList.some(p =>
                norm(p.playerId).includes(playerText) || norm(p.playerName).includes(playerText)
            );
        }
        if (bonusText) {
            bonusOk = Array.isArray(r.playerList) && r.playerList.some(p =>
                norm(p.bonusCode).includes(bonusText)
            );
        }
        return clientOk && nameOk && typeOk && statusOk && gameOk && currOk && rangeOk && playerOk && bonusOk;
    }


    // ========== Helpers  ==========
    let sortKey = '#';
    function setSort(key, preferredAsc = null){
        if (sortKey === key) {
            sortAsc = !sortAsc;
        } else {
            sortKey = key;
            const isText = (key === 'client' || key === 'campaign' || key === 'type' || key === 'game' || key === 'currency');
            sortAsc = (preferredAsc !== null) ? preferredAsc : isText;
        }
    }

    const caret = (key) => sortKey === key
        ? (sortAsc ? (icons.down || "↓") : (icons.up || "↑"))
        : "";

    function addRowNumbers(list){
        return (list || []).map((x, i) => ({ ...x, rowNumber: i + 1 }));
    }

    const getStartTs = (r) => r?.startDate ? new Date(r.startDate).getTime() : (r?.startTime || null);
    const getEndTs   = (r) => r?.endDate   ? new Date(r.endDate).getTime()   : null;

    function nnum(v){
        const n = Number(v);
        return Number.isFinite(n) ? n : null; // null al final
    }

    function sortLocal(list){
        let arr = Array.isArray(list) ? [...list] : [];

        switch (sortKey) {
            case '#':
                if (!sortAsc) arr.reverse();
                break;

            case 'client':
                arr.sort((a,b)=> sortAsc ? norm(a.clientLabel).localeCompare(norm(b.clientLabel))
                    : norm(b.clientLabel).localeCompare(norm(a.clientLabel)));
                break;

            case 'campaign':
                arr.sort((a,b)=> sortAsc ? norm(a.campaignName).localeCompare(norm(b.campaignName))
                    : norm(b.campaignName).localeCompare(norm(a.campaignName)));
                break;

            case 'type':
                arr.sort((a,b)=> sortAsc ? norm(a.campaignType).localeCompare(norm(b.campaignType))
                    : norm(b.campaignType).localeCompare(norm(a.campaignType)));
                break;

            case 'game':
                arr.sort((a,b)=> sortAsc ? norm(a.gameLabel).localeCompare(norm(b.gameLabel))
                    : norm(b.gameLabel).localeCompare(norm(a.gameLabel)));
                break;

            case 'currency':
                arr.sort((a,b)=> sortAsc ? norm(a.currencyLabel).localeCompare(norm(b.currencyLabel))
                    : norm(b.currencyLabel).localeCompare(norm(a.currencyLabel)));
                break;

            case 'amount':
                arr.sort((a,b)=>{
                    const A = nnum(a.amountLabel), B = nnum(b.amountLabel);
                    if (A === null && B === null) return 0;
                    if (A === null) return 1; // null/— al final
                    if (B === null) return -1;
                    return sortAsc ? (A - B) : (B - A);
                });
                break;

            case 'rounds':
                const toRoundsNum = (x) => {
                    if (x === '-' || x === '—') return 0;
                    const n = Number(x);
                    return Number.isFinite(n) ? n : null;
                };
                arr.sort((a,b)=>{
                    const A = toRoundsNum(a.roundsLabel), B = toRoundsNum(b.roundsLabel);
                    if (A === null && B === null) return 0;
                    if (A === null) return 1;
                    if (B === null) return -1;
                    return sortAsc ? (A - B) : (B - A);
                });
                break;

            case 'start':
                arr.sort((a,b)=>{
                    const A = getStartTs(a), B = getStartTs(b);
                    if (A === null && B === null) return 0;
                    if (A === null) return 1;
                    if (B === null) return -1;
                    return sortAsc ? (A - B) : (B - A); // asc = antiguo→reciente
                });
                break;

            case 'end':
                arr.sort((a,b)=>{
                    const A = getEndTs(a), B = getEndTs(b);
                    if (A === null && B === null) return 0;
                    if (A === null) return 1;
                    if (B === null) return -1;
                    return sortAsc ? (A - B) : (B - A);
                });
                break;

            case 'status':
                arr.sort((a,b)=>{
                    const A = Number(a.statusCode);
                    const B = Number(b.statusCode);
                    if (!Number.isFinite(A) && !Number.isFinite(B)) return 0;
                    if (!Number.isFinite(A)) return 1;
                    if (!Number.isFinite(B)) return -1;
                    return sortAsc ? (A - B) : (B - A);
                });
                break;
        }
        return addRowNumbers(arr);
    }

    function toggleSort(){
        setSort('#');
    }

    const norm = (s) => String(s ?? "")
        .normalize("NFD").replace(/\p{Diacritic}/gu, "")
        .toLowerCase().trim();
    export let persistStatus = true;
    export let reconcilePlayers = true;
    export let autoReconcileOnLoad = true;
    let _persistingIds = new Set();

    function getUpdateFn(){ return (api?.updateStatus || api?.updateStatusCampaign || api?.updateStatusCampaign || null); }
    function getUpdatePlayerFn(){ return (api?.updateStatusPlayer || null); }
    function getProviderDeleteFn(){ return (api?.providerDeletePlayer || null); }
    async function refetchOne(campaignId) {
        if (!api?.getById) return null;
        try {
            const res = await api.getById(campaignId);
            const raw = res?.data ?? res;
            return raw ? normalizeCampaignRow(raw) : null;
        } catch (_) {
            return null;
        }
    }
    async function executePlans(rows){
        const updateCampaign = getUpdateFn();
        const updatePlayer   = getUpdatePlayerFn();
        const providerDelete = getProviderDeleteFn();

        for (const r of (rows || [])) {
            const plan = planActionsForCampaign(r);
            const effectiveNow = Number(r?.statusCode ?? r?.baseStatus ?? 4);
            if (effectiveNow === 0) continue;
            if (persistStatus && typeof updateCampaign === 'function' && plan.campaignUpdate) {
                const { campaignId, newStatus } = plan.campaignUpdate;
                const currentBase = Number(r?.baseStatus ?? 4);
                if (Number(newStatus) === 0) {
                    console.debug("[SKIP][AUTO-DELETE]", campaignId);
                } else if (
                    campaignId &&
                    !_persistingIds.has(campaignId) &&
                    Number(newStatus) !== currentBase &&
                    Number(newStatus) !== effectiveNow
                ) {
                    _persistingIds.add(campaignId);
                    try {
                        await updateCampaign(campaignId, newStatus);
                        campaigns = (campaigns || []).map(x =>
                            String(x.campaignId) === String(campaignId)
                                ? { ...x, baseStatus: newStatus }
                                : x
                        );
                    } catch(_) {
                    } finally {
                        setTimeout(() => _persistingIds.delete(campaignId), 300);
                    }
                }
            }
            if (reconcilePlayers && typeof providerDelete === 'function' && Array.isArray(plan.providerDeletes)) {
                for (const it of plan.providerDeletes) {
                    const key = `${r.campaignId}|${it.playerId}`;
                    if (_providerDeletingKeys.has(key)) continue;
                    _providerDeletingKeys.add(key);
                    try {
                        await providerDelete({ campaign: r, player: it });
                    } catch(_) {
                    } finally {
                        setTimeout(() => _providerDeletingKeys.delete(key), 300);
                    }
                }
            }
            if (reconcilePlayers && typeof updatePlayer === 'function' && Array.isArray(plan.playerUpdates)) {
                for (const it of plan.playerUpdates) {
                    const key = `${r.campaignId}|${it.playerId}`;
                    if (_persistingPlayerKeys.has(key)) continue;
                    _persistingPlayerKeys.add(key);
                    try {
                        console.debug("[UPDATE PLAYER]", { campaignId: r.campaignId, sendingKey: it.bonusCode || it.playerId, newStatus: it.newStatus });
                        const res = await updatePlayer(it.bonusCode || it.playerId, it.newStatus, r);
                        const payload = res?.data ?? res;
                        if (payload?.error) {
                            console.warn("[UPDATE PLAYER] backend error", payload);
                            continue;
                        }
                        campaigns = (campaigns || []).map(x => {
                            if (String(x.campaignId) !== String(r.campaignId)) return x;
                            const nextPlayers = (x.playerList || []).map(pl =>
                                String(pl.playerId) === String(it.playerId)
                                    ? { ...pl, playerStatus: it.newStatus }
                                    : pl
                            );
                            return { ...x, playerList: nextPlayers };
                        });
                    } catch(_) {
                    } finally {
                        setTimeout(() => _persistingPlayerKeys.delete(key), 300);
                    }
                }
            }
        }
    }

    // ========== Actions ==========
    async function load() {
        if (!provider) { campaigns = []; return; }
        const seq = ++_loadSeq;
        isLoading = true;
        try {
            const sessionInfo = await getSessionClientsFromSession();
            const paramsBase = {};
            if (provider) paramsBase.provider = provider;
            if (Array.isArray(sessionInfo.codes) && sessionInfo.codes.length > 0) {
                paramsBase.clientCodes = sessionInfo.codes.join(',');
            } else if (Array.isArray(sessionInfo.ids) && sessionInfo.ids.length > 0) {
                paramsBase.clientIds = sessionInfo.ids.join(',');
            }
            const res = await api.listByProvider(provider, paramsBase);
            if (seq !== _loadSeq) return;
            const raw = res?.data?.list ?? res?.data ?? [];
            const mapped = raw.map(doc => {
                const base = normalizeCampaignRow(doc); // baseStatus = campaignStatus
                const startMs = base.startDate ? new Date(base.startDate).getTime() : (base.startTime || 0);
                const endMs   = base.endDate   ? new Date(base.endDate).getTime()   : 0;
                const effective = computeCampaignStatus(base.statusCode, startMs, endMs);
                const info = getStatusInfo(effective);
                const liveBudgetRaw = (base?.liveConfig?.totalBudget ?? doc?.liveConfig?.totalBudget);
                const slotAmount    = Number(base?.slotConfigs?.[0]?.amountPerRound ?? base?.slotConfig?.amountPerRound);
                const slotRounds    = Number(base?.slotConfigs?.[0]?.rounds ?? base?.slotConfig?.rounds);
                const isLive        = String(base?.campaignType).toUpperCase() === 'LIVE';
                const isBlankish = (v) => {
                    if (v === null || v === undefined) return true;
                    if (typeof v === 'string') {
                        const t = v.trim().toLowerCase();
                        return (t === '' || t === '-' || t === '—' || t === 'n/a');
                    }
                    return false;
                };
                const hasValue = (v) => !isBlankish(v);
                const liveBudget = (liveBudgetRaw !== null && liveBudgetRaw !== undefined) ? Number(liveBudgetRaw) : NaN;
                const amountLabel = hasValue(base?.amountLabel)
                    ? base.amountLabel
                    : (
                        isLive
                            ? (Number.isFinite(liveBudget) ? String(liveBudget) : '—')   // LIVE → totalBudget
                            : (Number.isFinite(slotAmount) ? String(slotAmount) : '—')   // SLOT → amountPerRound
                    );
                const roundsLabel = hasValue(base?.roundsLabel)
                    ? base.roundsLabel
                    : (
                        isLive
                            ? '-'                                                       // LIVE → sin rondas
                            : (Number.isFinite(slotRounds) ? String(slotRounds) : '—')  // SLOT → rounds
                    );
                return {
                    ...base,
                    amountLabel,
                    roundsLabel,
                    baseStatus: base.statusCode,
                    statusCode: effective,
                    statusInfoClass: info.class,
                    statusInfoLabel: info.label,
                    _configExpanded: false
                };
            });
            _rows = mapped;
            if (autoReconcileOnLoad) await executePlans(_rows);
            if (seq !== _loadSeq) return;
            dispatch("loaded", { campaigns: campaigns });
        } finally {
            if (seq === _loadSeq) isLoading = false;
        }
    }
    let _lastToken = "";
    $: {
        const token = JSON.stringify({
            extraParams: extraParams || {},
            filters: filtersForServer(),
            search
        });
        if (token !== _lastToken) {
            _lastToken = token;
            scheduleAutoLoad();
        }
    }
    $: {
        _filtersToken;
        sortKey;
        sortAsc;
        const needle = norm(search);
        const base = Array.isArray(_rows) ? _rows : [];
        const locallyFiltered = base.filter(r => {
            const byNeedle = needle
                ? [r.campaignName, r.clientLabel, r.campaignType, r.gameLabel, r.currencyLabel]
                    .map(v => norm(v)).join("|").includes(needle)
                : true;
            return byNeedle && passesFilters(r);
        });
        const sorted = sortLocal(locallyFiltered);
        const total = sorted.length;
        const totalPages = Math.max(1, Math.ceil(total / pageSize));
        if (page > totalPages) page = totalPages;
        const pageStart = (page - 1) * pageSize;
        const pageEnd   = Math.min(pageStart + pageSize, total);
        campaigns = sorted.slice(pageStart, pageEnd);
        numberAt = (iLocal) => {
            const globalIdx = pageStart + iLocal; // 0-based
            return (sortKey === '#' && !sortAsc)
                ? (total - globalIdx)
                : (globalIdx + 1);
        };
        pagerInfo = { total, totalPages, pageStart, pageEnd };
    }


    /*function toggleSort(){
        sortAsc = !sortAsc;
        campaigns = sortAndNumber(campaigns, sortAsc);
    }*/
    async function cancelCampaign(fb) {
        if (!allowCancel) return;
        if (!confirm(`¿Seguro que deseas eliminar la campaña ${fb.campaignName}?`)) return;
        try {
            await api.cancelCampaign(fb.campaignId, fb);
            await load();
            dispatch("cancelled", { campaignId: fb.campaignId });
        } catch (err) {
            console.error(err);
            alert("No se pudo eliminar la campaña.");
        }
    }
    async function openPlayers(fb){
        dispatch("openplayers", fb);
    }
    function openCreate(){ dispatch("create"); }
    function toggleConfig(fb) {
        campaigns = campaigns.map(c =>
            c.campaignId === fb.campaignId
                ? { ...c, _configExpanded: !c._configExpanded }
                : c
        );
    }
    onMount(() => {
        autoSearch = true;
        load();
        const handler = () => { refresh(); };
        window.addEventListener('campaign:created', handler);
        return () => window.removeEventListener('campaign:created', handler);
    });
</script>

<!-- ====== Barra de acciones ====== -->
<div class="wrapp">
    <div class="menu-filter">
        <div class="row-top">
            <!-- Columna izquierda: acciones -->
            <div class="actions-col">
                <div class="search-actions">
                    {#if showCreate}
                        <button class="btn btn-pink btn-sm" on:click={openCreate} title="Crear Campaña">
                            {@html (icons.add || "+")}
                        </button>
                    {/if}
                </div>
            </div>

            <!-- Columna derecha: filtros rápidos (exactamente 5 celdas) -->
            <div class="filters-row-quick">
                <div class="f-item">
                    <label for="clientOption">Cliente</label>
                    <input id="clientOption" class="form-control" list="clientsList" placeholder="Nombre o código" bind:value={filters.client} />
                    <datalist id="clientsList">
                        {#each clientOptions as c}<option value={c}></option>{/each}
                    </datalist>
                </div>

                <div class="f-item">
                    <label for="campaignOption">Campaña</label>
                    <input id="campaignOption" class="form-control" placeholder="Ej. Black Friday" bind:value={filters.campaignName} />
                </div>

                <div class="f-item">
                    <label for="typeOption">Tipo</label>
                    <select id="typeOption" class="form-select" bind:value={filters.type}>
                        <option value="">Todos</option>
                        {#each TYPE_OPTIONS as t}<option value={t}>{t}</option>{/each}
                    </select>
                </div>

                <div class="f-item">
                    <label for="gameOption">Juego</label>
                    <input id="gameOption" class="form-control" placeholder="Nombre del juego" bind:value={filters.game} />
                </div>

                <div class="f-item">
                    <label for="statusOption">Estado</label>
                    <div id="statusOption" class="ms" use:clickOutside on:keydown={(e)=> e.key==='Escape' && (statusOpen=false)}>
                        <button type="button"
                                class="form-control ms-trigger"
                                aria-haspopup="listbox"
                                aria-expanded={statusOpen}
                                on:click={()=> statusOpen = !statusOpen}>
                            {statusSummary}
                            <span class="ms-caret">▾</span>
                        </button>

                        {#if statusOpen}
                            {#key statusesKey}
                                <div class="ms-panel" role="listbox" tabindex="-1"
                                     on:focusout={(e)=> { if(!e.currentTarget.contains(e.relatedTarget)) statusOpen=false; }}>
                                    <label class="ms-option">
                                        <input
                                                type="checkbox"
                                                checked={allSelected}
                                                on:change={(e) => toggleAll(e.currentTarget.checked)}
                                        />
                                        Todos
                                    </label>

                                    <!-- Grupo real con bind:group -->
                                    {#each STATUS_OPTIONS as s}
                                        <label class="ms-option">
                                            <input
                                                    type="checkbox"
                                                    value={s.value}
                                                    bind:group={filters.statuses}
                                            />
                                            {s.label}
                                        </label>
                                    {/each}
                                </div>
                            {/key}
                        {/if}
                    </div>
                </div>
            </div>

            <!-- Columna derecha: acciones -->
            <div class="actions-col">
                <div class="f-item tight">
                    <label for="filtersOption">&nbsp;</label>
                    <button
                            id="filtersOption"
                            class="btn btn-pink btn-sm"
                            aria-pressed={showMoreFilters}
                            on:click={() => (showMoreFilters = !showMoreFilters)}
                    >
                        {@html (showMoreFilters ? (icons.funnelCircleX || "✖️") : (icons.filter || "⚲"))}
                    </button>
                </div>
                <!-- Limpiar filtros -->
                <div class="search-actions">
                    <button class="btn btn-pink btn-sm"
                            on:click={clearAllFilters}
                            title="Limpiar filtros">
                        {@html (icons.clean || "🧹")}
                    </button>
                </div>

            </div>
        </div>

        {#if showMoreFilters}
            <div class="filters-row more">
                <div class="f-item">
                    <label for="currencyOption">Moneda</label>
                    <select id="currencyOption" class="form-select" bind:value={filters.currency}>
                        <option value="">Todas</option>
                        {#each currencyOptions as cur}<option value={cur}>{cur}</option>{/each}
                    </select>
                </div>

                <div class="f-item">
                    <label for="playerOption">Jugador</label>
                    <input id="playerOption" class="form-control" placeholder="ID o nombre" bind:value={filters.player} />
                </div>

                <div class="f-item">
                    <label for="bonusOption">Bonus ID</label>
                    <input id="bonusOption" class="form-control" placeholder="Bonus ID" bind:value={filters.bonus} />
                </div>

                <div class="f-item">
                    <label for="startTimeOption">Desde</label>
                    <input id="startTimeOption" type="datetime-local" class="form-control" bind:value={filters.dateFrom} />
                </div>

                <div class="f-item">
                    <label for="endTimeOption">Hasta</label>
                    <input id="endTimeOption" type="datetime-local" class="form-control" bind:value={filters.dateTo} />
                </div>
            </div>
        {/if}
    </div>
</div>

<!-- ====== Tabla ====== -->
{#if isLoading}
    <p>Cargando…</p>
{:else if campaigns.length === 0}
    <p class="text-center text-muted">No se encontraron campañas.</p>
{:else}
    <div class="table-wrapper">
        <table class="table table-striped table-hover table-bordered align-middle">
            <thead>
            <tr>
                <!-- Índice -->
                <th class="th-sort" on:click={() => setSort('#')}>
                    N°
                    <span class="hdr-ico">{@html (icons.sort)}</span>
                </th>

                <!-- Cliente -->
                <th class="th-sort" on:click={() => setSort('client', true)}>
                    Cliente
                    <span class="hdr-ico">{@html (icons.sort)}</span>
                </th>

                <!-- Campaña -->
                <th class="th-sort" on:click={() => setSort('campaign', true)}>
                    Campaña
                    <span class="hdr-ico">{@html (icons.sort)}</span>
                </th>

                <!-- Tipo -->
                <th class="th-sort" on:click={() => setSort('type', true)}>
                    Tipo
                    <span class="hdr-ico">{@html (icons.sort)}</span>
                </th>

                <!-- Configuración -->
                <th class="th-sort" on:click={() => setSort('game', true)}>
                    Juegos / Monto / Rondas
                    <span class="hdr-ico">{@html (icons.sort)}</span>
                </th>

                <!-- Moneda -->
                <th class="th-sort" on:click={() => setSort('currency', true)}>
                    Moneda
                    <span class="hdr-ico">{@html (icons.sort)}</span>
                </th>

                <!-- Inicio -->
                <th class="th-sort" on:click={() => setSort('start', false)}>
                    Inicio
                    <span class="hdr-ico">{@html (icons.sort)}</span>
                </th>

                <!-- Fin -->
                <th class="th-sort" on:click={() => setSort('end', false)}>
                    Fin
                    <span class="hdr-ico">{@html (icons.sort)}</span>
                </th>

                <!-- Estado -->
                <th class="th-sort" on:click={() => setSort('status', false)}>
                    Estado
                    <span class="hdr-ico">{@html (icons.sort)}</span>
                </th>
                <th>Acciones</th>
            </tr>
            </thead>
            <tbody>
            {#each campaigns as fb, i (fb.campaignId)}
                <tr>
                    <td>{ (sortKey === '#' && !sortAsc)
                            ? (campaigns.length - i)
                            : (i + 1)
                        }</td>
                    <td>{fb.clientLabel}</td>
                    <td>{fb.campaignName || '—'}</td>
                    <td>{fb.campaignType}</td>
                    <td>
                        {#if fb.gameConfigs && fb.gameConfigs.length}
                            {@const limit = 2}
                            {@const total = fb.gameConfigs.length}
                            {@const showAll = fb._configExpanded || total <= limit}
                            <div class="config-table">
                                {#each fb.gameConfigs as cfg, idx}
                                    {#if showAll || idx < limit}
                                        <div class="config-card compact">
                                            <span class="config-game" title={cfg.game}>{cfg.game}</span>
                                            <span class="config-meta">
                                                <span class="config-meta-item" title="Monto por ronda">
                                                    <small>Monto</small> {cfg.amount}
                                                </span>
                                                <span class="config-meta-item" title="Rondas">
                                                    <small>Rondas</small> {cfg.rounds}
                                                </span>
                                            </span>
                                        </div>
                                    {/if}
                                {/each}
                            </div>
                            {#if total > limit}
                                <button class="btn btn-link btn-sm config-toggle" on:click|stopPropagation={() => toggleConfig(fb)}>
                                    {showAll ? `Ver menos` : `Ver más (${total - limit})`}
                                </button>
                            {/if}
                        {:else}
                            <span class="text-muted">—</span>
                        {/if}
                    </td>
                    <td>{fb.currencyLabel}</td>
                    <td title={fb.startDate ? moment(fb.startDate).local().format("dddd D [de] MMMM YYYY, hh:mm A") : ''}>
                        <span class="date-cell">{fb.startDate ? moment(fb.startDate).local().format(timeFormat) : '—'}</span>
                    </td>
                    <td title={fb.endDate ? moment(fb.endDate).local().format("dddd D [de] MMMM YYYY, hh:mm A") : ''}>
                        <span class="date-cell">{fb.endDate ? moment(fb.endDate).local().format(timeFormat) : '—'}</span>
                    </td>

                    <td>
                        <span class={`badge status-badge rounded-pill ${fb.statusInfoClass}`}>
                            <span class="status-dot"></span>
                            {fb.statusInfoLabel}
                        </span>
                    </td>
                    <td class="actions-cell">
                        <div class="actions-content">
                            <button class="btn btn-icon btn-view" title="Ver jugadores" on:click={() => openPlayers(fb)}>
                                {@html (icons.persons)}
                            </button>
                            {#if allowCancel}
                                <button class="btn btn-icon btn-delete"
                                        disabled={+fb.statusCode === 0 || +fb.statusCode === 3 || +fb.statusCode === 5}
                                        on:click={() => cancelCampaign(fb)}
                                        title="Eliminar campaña">
                                    {@html (icons.delete)}
                                </button>
                            {/if}
                        </div>
                    </td>
                </tr>
            {/each}
            </tbody>
        </table>
    </div>
{/if}

<div class="pagination-bar">
    <div class="pag-left">
        <span class="total-badge">Total: <strong>{pagerInfo.total}</strong> campaña{pagerInfo.total === 1 ? '' : 's'}</span>
    </div>

    <div class="pag-center">
        <div class="pager-group" role="group" aria-label="Paginación">
            <button type="button" class="pager-btn" aria-label="Primera página"
                    on:click={() => page = 1} disabled={page === 1}>
                <span class="ico" aria-hidden="true">{@html icons.firstPage}</span>
            </button>
            <button type="button" class="pager-btn" aria-label="Página anterior"
                    on:click={() => page = Math.max(1, page - 1)} disabled={page === 1}>
                <span class="ico" aria-hidden="true">{@html icons.prevPage}</span>
            </button>
        </div>

        <span class="page-info">
            Página <strong>{page}</strong> de <strong>{Math.max(1, Math.ceil(pagerInfo.total / pageSize))}</strong>
        </span>

        <div class="pager-group" role="group" aria-label="Paginación">
            <button type="button" class="pager-btn" aria-label="Página siguiente"
                    on:click={() => page = page + 1}
                    disabled={page >= Math.ceil(pagerInfo.total / pageSize)}>
                <span class="ico" aria-hidden="true">{@html icons.nextPage}</span>
            </button>
            <button type="button" class="pager-btn" aria-label="Última página"
                    on:click={() => page = Math.ceil(pagerInfo.total / pageSize)}
                    disabled={page >= Math.ceil(pagerInfo.total / pageSize)}>
                <span class="ico" aria-hidden="true">{@html icons.lastPage}</span>
            </button>
        </div>
    </div>

    <div class="pag-right">
        <span class="range-text">
            {pagerInfo.total ? pagerInfo.pageStart + 1 : 0} – {pagerInfo.pageEnd} de {pagerInfo.total}
        </span>
        <div class="size-select">
            <select id="sizePag" bind:value={pageSize} on:change={() => { pageSize = +pageSize; page = 1; }}>
                {#each pageSizeOptions as opt}
                    <option value={opt}>{opt} / pág.</option>
                {/each}
            </select>
        </div>
    </div>
</div>

<style>
    .th-sort { cursor:pointer; user-select:none; white-space:nowrap; }
    .th-sort .hdr-ico { margin-left:.35rem; font-size:.9em; opacity:.65; display:inline-flex; }
    .wrapp { padding: 10px; margin: 0; }
    .menu-filter { display: flex; flex-wrap: wrap; gap: 0.5rem; padding: 0.5rem; margin: 0; }
    .btn-pink { background-color: #2a1a28; color: white; font-size: 0.80rem; padding: 0.5rem 1rem; border-radius: 6px; box-shadow: 0 4px 8px rgba(0,0,0,0.1); }
    .btn-pink:hover { background-color: #FF0090; color: white; }
    .search-actions { display: flex; justify-content: flex-start; align-items: center; gap: 0.5rem; margin-top: 1rem; }
    input.form-control{ font-size: 1rem; padding: 0.4rem 0.6rem; }
    .ms { position: relative; }
    .ms-trigger{
        display:flex; align-items:center; justify-content:space-between;
        cursor:pointer; user-select:none;
        white-space: nowrap;
        overflow: hidden;
        text-overflow: ellipsis;
    }
    .ms-caret{ flex-shrink: 0; opacity:.6; margin-left:.5rem; }
    .ms-panel{
        position:absolute; inset:auto 0 auto 0;
        top: calc(100% + 6px);
        background:#fff; border:1px solid var(--bs-border-color, #e5e7eb);
        border-radius:10px; padding:.5rem; z-index:30;
        box-shadow: 0 6px 22px rgba(0,0,0,.08);
    }
    .ms-option{
        display: block;
        padding: .15rem .6rem;
        cursor: pointer;
        border-radius: 6px;
        white-space: nowrap;
    }
    .ms-option:hover{ background:#f6f7f9; }
    .ms-option input[type="checkbox"]{
        width: 16px;
        height: 16px;
        margin: 0 .5rem 0 0;
        vertical-align: middle;
    }
    .menu-filter {
        display: block;
        padding: .75rem;
        background: #fff;
        border: 1px solid var(--bs-border-color, #e5e7eb);
        border-radius: .5rem;
        margin-bottom: .5rem;
    }
    .row-top{
        display:grid;
        grid-template-columns: auto 1fr auto;
        gap:.75rem;
        align-items:end;
    }
    .actions-col{
        display: flex;
        align-items: end;
        gap: .5rem;
        flex-wrap: wrap;
    }
    .filters-row-quick{
        display: grid;
        grid-template-columns: repeat(5, minmax(140px, 1fr));
        gap: .6rem;
        align-items: end;
    }
    .filters-row.more{
        display:grid;
        grid-template-columns: repeat(5, minmax(140px, 1fr));
        gap: .6rem;
        padding: .75rem;
        margin-top: .5rem;
        background: #fafbfc;
        border: 1px dashed #e2e6ea;
        border-radius: .4rem;
    }
    .f-item {
        display: flex;
        flex-direction: column;
    }
    .f-item label{
        font-size:.7rem;
        color:#666;
        margin-bottom:.3rem;
        display:block;
        font-weight: 600;
        text-transform: uppercase;
        letter-spacing: 0.02em;
    }
    .f-item :is(input, select, .ms-trigger) {
        font-size: 0.85rem;
        padding: .4rem .55rem;
        border: 1px solid var(--bs-border-color, #e2e6ea);
        border-radius: .4rem;
        background: #fff;
        color: #333;
        min-height: 38px;
    }
    .f-item :is(input, select, .ms-trigger):focus {
        outline: none;
        border-color: #86b7fe;
        box-shadow: 0 0 0 3px rgba(13,110,253,.1);
    }
    .f-item.tight label{ visibility:hidden; height:0; margin:0; display:block; }
    .search-actions{ display: flex; align-items: end; gap: .5rem; margin-top: 0; }
    .ms-trigger.form-control {
        width: 100%;
        text-align: left;
        cursor: pointer;
        display: flex;
        align-items: center;
        justify-content: space-between;
    }
    @media (max-width:1200px){
        .row-top{ grid-template-columns:1fr; }
        .filters-row-quick,
        .filters-row.more{
            grid-template-columns: repeat(3, 1fr);
        }
    }
    @media (max-width:640px){
        .filters-row-quick,
        .filters-row.more{
            grid-template-columns: repeat(2, 1fr);
        }
    }
    /* ===== Paginación ===== */
    .pagination-bar {
        display: flex;
        align-items: center;
        justify-content: space-between;
        gap: .75rem;
        padding: .65rem .75rem;
        margin-top: .5rem;
        background: #fff;
        border: 1px solid var(--bs-border-color, #e5e7eb);
        border-radius: .5rem;
        flex-wrap: wrap;
    }
    .pag-left, .pag-right, .pag-center {
        display: flex;
        align-items: center;
        gap: .5rem;
        min-width: max-content;
    }
    .pag-center {
        flex: 1 1 auto;
        justify-content: center;
        gap: .75rem;
    }
    .total-badge {
        font-size: 0.8rem;
        color: #555;
        background: #f8f9fa;
        border: 1px solid #e5e7eb;
        padding: .35rem .65rem;
        border-radius: 50rem;
    }
    .total-badge strong {
        color: #111;
    }
    .page-info {
        font-size: 0.85rem;
        color: #555;
        white-space: nowrap;
    }
    .page-info strong {
        color: #111;
    }
    .range-text {
        font-size: 0.8rem;
        color: #777;
        white-space: nowrap;
    }
    .pager-group {
        display: inline-flex;
        gap: .25rem;
        align-items: center;
    }
    .pager-btn {
        --size: 34px;
        width: var(--size);
        height: var(--size);
        min-width: var(--size);
        min-height: var(--size);
        display: inline-flex;
        align-items: center;
        justify-content: center;
        padding: 0;
        border: 1px solid var(--bs-border-color, #e5e7eb);
        background: #fff;
        border-radius: .4rem;
        cursor: pointer;
        line-height: 1;
        user-select: none;
        transition: transform .04s ease, background-color .15s ease, border-color .15s ease, box-shadow .15s ease;
    }
    .pager-btn .ico {
        display: inline-flex;
        pointer-events: none;
        font-size: 15px;
        color: #444;
    }
    .pager-btn:hover:not(:disabled) {
        background: #f4f7ff;
        border-color: #b6d4fe;
    }
    .pager-btn:active:not(:disabled) {
        transform: translateY(1px);
    }
    .pager-btn:focus-visible {
        outline: 2px solid #2563eb;
        outline-offset: 2px;
    }
    .pager-btn:disabled {
        opacity: .45;
        cursor: not-allowed;
        background: #f5f5f5;
    }
    .size-select select {
        font-size: 0.8rem;
        padding: .3rem 1.6rem .3rem .55rem;
        border: 1px solid var(--bs-border-color, #e5e7eb);
        border-radius: .4rem;
        background: #fff;
        color: #444;
        cursor: pointer;
        appearance: none;
        -webkit-appearance: none;
        background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='12' viewBox='0 0 16 16'%3E%3Cpath fill='%23666' d='M1.646 4.646a.5.5 0 0 1 .708 0L8 10.293l5.646-5.647a.5.5 0 0 1 .708.708l-6 6a.5.5 0 0 1-.708 0l-6-6a.5.5 0 0 1 0-.708z'/%3E%3C/svg%3E");
        background-repeat: no-repeat;
        background-position: right .45rem center;
    }
    .date-cell {
        white-space: nowrap;
    }
    @media (max-width: 820px) {
        .pagination-bar {
            flex-direction: column;
            align-items: stretch;
        }
        .pag-left, .pag-right, .pag-center {
            justify-content: center;
        }
        .pag-right {
            justify-content: space-between;
        }
    }
    .config-table {
        display: flex;
        flex-direction: column;
        gap: 0.45rem;
        min-width: 220px;
        max-width: 320px;
    }
    .config-card {
        display: flex;
        flex-direction: column;
        gap: 0.2rem;
        padding: 0.45rem 0.55rem;
        border-radius: 0.4rem;
        background: #fff;
        border: 1px solid #e9ecef;
        box-shadow: 0 1px 2px rgba(0,0,0,0.03);
        font-size: 0.8rem;
    }
    .config-card.compact {
        flex-direction: row;
        align-items: center;
        gap: 0.6rem;
        padding: 0.25rem 0.45rem;
        font-size: 0.78rem;
    }
    .config-game {
        color: #0d6efd;
        font-weight: 600;
        overflow: hidden;
        text-overflow: ellipsis;
        white-space: nowrap;
        min-width: 80px;
        max-width: 130px;
    }
    .config-card.compact .config-game {
        flex: 1 1 auto;
        max-width: 140px;
    }
    .config-meta {
        display: flex;
        gap: 0.5rem;
        flex-shrink: 0;
    }
    .config-meta-item {
        display: flex;
        align-items: baseline;
        gap: 0.25rem;
        background: #f8f9fa;
        border-radius: 0.25rem;
        padding: 0.15rem 0.35rem;
        font-weight: 600;
        color: #333;
        white-space: nowrap;
    }
    .config-meta-item small {
        font-size: 0.62rem;
        color: #888;
        text-transform: uppercase;
        letter-spacing: 0.03em;
        font-weight: 600;
    }
    .config-meta-item:nth-child(1) {
        color: #198754;
    }
    .config-meta-item:nth-child(2) {
        color: #ea580c;
    }
    .config-toggle {
        padding: 0;
        font-size: 0.75rem;
        color: #FF0090;
        text-decoration: none;
        font-weight: 600;
    }
    .config-toggle:hover {
        color: #d9007a;
        text-decoration: underline;
    }

    /* ===== Mejoras visuales de tabla ===== */
    .table-wrapper {
        overflow: auto;
        max-height: 70vh;
        border-radius: 0.5rem;
        border: 1px solid var(--bs-border-color, #e5e7eb);
    }
    .table-wrapper table {
        margin-bottom: 0;
    }
    thead th {
        position: sticky;
        top: 0;
        z-index: 2;
        background: #fff;
        box-shadow: 0 1px 0 #dee2e6;
        font-size: 0.78rem;
        text-transform: uppercase;
        letter-spacing: 0.02em;
        color: #555;
        font-weight: 700;
        padding: 0.6rem 0.5rem;
        border-bottom: none;
    }
    tbody tr {
        transition: background-color 0.12s ease;
    }
    tbody tr:hover {
        background-color: #f4f7ff !important;
    }
    tbody td {
        font-size: 0.85rem;
        padding: 0.35rem 0.5rem;
        vertical-align: middle;
    }

    /* Badges de estado */
    .status-badge {
        display: inline-flex;
        align-items: center;
        gap: 0.35rem;
        padding: 0.35em 0.75em;
        font-size: 0.75rem;
        font-weight: 600;
        border-radius: 50rem;
    }
    .status-dot {
        width: 6px;
        height: 6px;
        border-radius: 50%;
        background: currentColor;
        opacity: 0.85;
    }

    /* Acciones con iconos */
    .actions-cell {
        vertical-align: middle;
    }
    .actions-content {
        display: flex;
        gap: 0.35rem;
        justify-content: center;
        align-items: center;
    }
    .btn-icon {
        width: 32px;
        height: 32px;
        padding: 0;
        display: inline-flex;
        align-items: center;
        justify-content: center;
        border-radius: 8px;
        border: 1px solid transparent;
        transition: all 0.15s ease;
        background: transparent;
        cursor: pointer;
    }
    .btn-icon :global(svg),
    .btn-icon :global(i) {
        width: 16px;
        height: 16px;
        display: inline-flex;
    }
    .btn-view {
        color: #0d6efd;
        border-color: #cfe2ff;
        background: #f0f7ff;
    }
    .btn-view:hover {
        background: #0d6efd;
        color: #fff;
        border-color: #0d6efd;
    }
    .btn-delete {
        color: #dc3545;
        border-color: #f1aeb5;
        background: #fff5f5;
    }
    .btn-delete:hover:not(:disabled) {
        background: #dc3545;
        color: #fff;
        border-color: #dc3545;
    }
    .btn-icon:disabled {
        opacity: 0.45;
        cursor: not-allowed;
    }
</style>
